'use client';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ViewState } from 'react-map-gl';

interface Pole {
  ID: string;
  lat: string;
  lon: string;
  type: string;
  ny_status: string;
  merknad: string;
}

interface User {
  id: string;
  email: string;
  name: string;
}

interface MapState {
  viewport: ViewState;
  selectedPoleId: string | null;
}

interface PolesState {
  data: Pole[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

interface AuthState {
  isLoggedIn: boolean;
  token: string | null;
  user: User | null;
  error: string | null;
}

interface AppState {
  auth: AuthState;
  jobId: string;
  poles: PolesState;
  mapState: MapState;
  currentPageTitle: string; // Added for TopNav integration
  isMapView: boolean; // Flag to indicate if we're in map view
  setMapState: (updater: (prevState: MapState) => MapState) => void;
  setJobId: (newJobId: string) => void;
  setSelectedPoleId: (poleId: string | null) => void;
  getCurrentPole: () => Pole | undefined;
  setPageTitle: (title: string) => void; // Method to update page title
  setIsMapView: (isMapView: boolean) => void; // Method to update map view state

  //Holds the name of the selected company
  selectedCompany: string;
  setSelectedCompany: (company: string) => void;

  //Array of company names to populate the dropdown in TopNav
  companies: string[];
  setCompanies: (companies: string[]) => void;
  hasHydrated: boolean;
  setHasHydrated: (hasHydrated: boolean) => void;
}

const useStore = create<AppState>()(persist((set, get) => ({
  auth: {
    isLoggedIn: false,
    token: null,
    user: null,
    error: null,
  },
  jobId: 'job123',
  poles: {
    data: [],
    status: 'idle',
    error: null,
  },
  mapState: {
    viewport: {
      latitude: 60.3913,
      longitude: 5.3221,
      zoom: 5,
      bearing: 0,
      pitch: 0,
      padding: { top: 0, bottom: 0, left: 0, right: 0 },
    },
    selectedPoleId: null,
  },
  currentPageTitle: 'Dashboard',
  isMapView: false,

  setJobId: (newJobId) => set({ jobId: newJobId }),

  setSelectedPoleId: (poleId) =>
    set((state) => ({
      mapState: { ...state.mapState, selectedPoleId: poleId },
    })),

  // Correct implementation for getCurrentPole
  getCurrentPole: () => {
    const { mapState, poles } = get();
    return poles.data.find((pole) => pole.ID === mapState.selectedPoleId);
  },

  // Corrected implementation for functional update
  setMapState: (updater) =>
    set((prevState) => ({ mapState: updater(prevState.mapState) })),
    
  // Add setter for page title
  setPageTitle: (title) => set({ currentPageTitle: title }),
  
  // Add setter for map view flag
  setIsMapView: (isMapView) => set({ isMapView }),

  // Initial state for selected company and companies
  selectedCompany: '',
  setSelectedCompany: (company) => set({ selectedCompany: company }),
  companies: [],
  setCompanies: (companies) => set({ companies }),
  hasHydrated: false,
  setHasHydrated: (hasHydrated) => set({ hasHydrated }),
}), {
  name: 'vigi-dashboard-store',
  storage: createJSONStorage(() => localStorage),
  onRehydrateStorage: () => (state) => {
    state?.setHasHydrated(true);
  },
  partialize: (state) => ({
    selectedCompany: state.selectedCompany,
  }),
}));

export default useStore;
